import * as mat from './lib/jsmatrix';
import { MatF32, VecF32 } from './lib/jsmatrix';

function distanceSquaredPointLineSegment(p, a, b) {
    const dir = mat.fromTo(a, b);
    const pb = mat.fromTo(p, b);

    let t = mat.dot(dir, pb);
    // closest point is b
    if (t <= 0) {
        return mat.norm2Squared(pb);
    }


    const pa = mat.fromTo(p, a);

    t = mat.dot(dir, pa);
    // closest point is a
    if (t >= 0) {
        return mat.norm2Squared(pa);
    }

    const d2 = mat.dot(dir, dir);

    const perpSegment = mat.sub(pa, mat.scale(dir, mat.dot(pa, dir) / d2));

    return mat.norm2Squared(perpSegment);
}

function transformPoints(p, M) {
    p = mat.PaddedView.new(p, 4, p.cols(), 1, 1);
    p = mat.mult(M, p);
    p = mat.homogenize(p);

    return MatF32.copy(mat.block(p, 0, 0, 3, p.cols()));
}

/**
 * 
 * @param {Mat} P 
 * @param {*} normalize 
 */
function computeFrustumPlanes(P, normalize) {
    const result = [];
    // Left clipping plane
    result.push(VecF32.from([P.at(3, 0) + P.at(0, 0),
    P.at(3, 1) + P.at(0, 1), P.at(3, 2) + P.at(0, 2), P.at(3, 3) + P.at(0, 3)]));

    // Right clipping plane
    result.push(VecF32.from([P.at(3, 0) - P.at(0, 0),
    P.at(3, 1) - P.at(0, 1),
    P.at(3, 2) - P.at(0, 2),
    P.at(3, 3) - P.at(0, 3)]));

    // Top clipping plane
    result.push(VecF32.from([P.at(3, 0) - P.at(1, 0),
    P.at(3, 1) - P.at(1, 1),
    P.at(3, 2) - P.at(1, 2),
    P.at(3, 3) - P.at(1, 3)]));

    // Bottom clipping plane
    result.push(VecF32.from([P.at(3, 0) + P.at(1, 0),
    P.at(3, 1) + P.at(1, 1),
    P.at(3, 2) + P.at(1, 2),
    P.at(3, 3) + P.at(1, 3)]));

    // Near clipping plane
    result.push(VecF32.from([P.at(3, 0) + P.at(2, 0),
    P.at(3, 1) + P.at(2, 1),
    P.at(3, 2) + P.at(2, 2),
    P.at(3, 3) + P.at(2, 3)]));

    // Far clipping plane
    result.push(VecF32.from([P.at(3, 0) - P.at(2, 0),
    P.at(3, 1) - P.at(2, 1),
    P.at(3, 2) - P.at(2, 2),
    P.at(3, 3) - P.at(2, 3)]));

    if (normalize) {
        for (let i = 0; i < result.length; i++) {
            const mag = mat.norm(mat.subvec(result[i], 0, 3));
            mat.scale(result[i], 1.0 / mag);
        }
    }

    return result;
}

function pointInBounds2d(p, bmin, bmax) {

    for (let i = 0; i < 2; i++) {
        let pi = p.at(i);
        if (pi < bmin.at(i) || pi > bmax.at(i)) {
            return false;
        }
    }

    return true;
}


function computeBounds(points) {
    const bmin = mat.rowreduce(points, row => mat.min(row));
    const bmax = mat.rowreduce(points, row => mat.max(row));


    return [bmin, bmax]
}

function intersectLinePlane(a, b, plane) {
    const l = mat.sub(b, a);
    const d = -mat.dot(plane, mat.hvec(a, 1)) / mat.dot(mat.hvec(l, 0), plane);

    return [mat.add(a, mat.scale(l, d)), d];
}

function planeFromPointNormal(p, n) {
    const result = VecF32.uninitialized(4);
    mat.insert(mat.subvec(result, 0, 3), n);
    result.set(-mat.dot(n, p), 3);
    return result;
}

// From rosetta code for now: https://rosettacode.org/wiki/Ramer-Douglas-Peucker_line_simplification#JavaScript
/**
 * @param {!Array<pointType>} l
 * @param {number} eps
 */
function simplifyLine(l, eps) {
    const last = l.length - 1;
    const p1 = l[0];
    const p2 = l[last];
    const x21 = p2.at(0) - p1.at(0);
    const y21 = p2.at(1) - p1.at(1);

    const [dMax, x] = l.slice(1, last)
        .map(p => Math.abs(y21 * p.at(0) - x21 * p.at(1) + p2.at(0) * p1.at(1) - p2.at(1) * p1.at(0)))
        .reduce((p, c, i) => {
            const v = Math.max(p[0], c);
            return [v, v === p[0] ? p[1] : i + 1];
        }, [-1, 0]);

    if (dMax > eps) {
        return [...simplifyLine(l.slice(0, x + 1), eps), ...simplifyLine(l.slice(x), eps).slice(1)];
    }
    return [l[0], l[last]]
};
function transformPlane(plane, M) {
    return mat.mult(mat.transpose(mat.inv(M)), plane);
}

function pointSide(a, plane) {
    return Math.sign(mat.dot(mat.hvec(a, 1), plane));
}

function pointInside(a, plane) {
    return pointSide(a, plane) >= 0;
}

function mat2points(m) {
    let drawpoints = [];
    mat.colwise(m, (c) => {
        drawpoints.push([c.at(0), c.at(1)]);
    });

    return drawpoints;
}

function isClockwise(parray) {
    let sum = 0.0;
    // from computational geometry in  c
    const n = parray.length;
    for (let i = 0; i < n; i++) {
        const a = parray[i];
        const b = parray[(i + 1) % n];
        sum += (a.at(0) + b.at(0)) * (b.at(1) - a.at(1));
    }

    return sum < 0.0;
}

function isLeft(P0, P1, P2) {
    return ((P1.at(0) - P0.at(0)) * (P2.at(1) - P0.at(1))
        - (P2.at(0) - P0.at(0)) * (P1.at(1) - P0.at(1)));
}

function normal2D(v) {
    return VecF32.from([-v.at(1), v.at(0), 0]);
}
function addz(p, z = 0) {
    return VecF32.from([p.at(0), p.at(1), z]);
}

function closestVertex(objects, r2, pWorld) {

    let closest = -1;
    let closestRadius = r2;
    for (let i = 0; i < objects.length; i++) {
        const pi = objects[i];
        const di2 = mat.norm2Squared(mat.sub(pi, pWorld));
        if (di2 < closestRadius) {
            closest = i;
            closestRadius = di2;
        }
    }

    return closest;
}


function findIntersectSet(objects, mousePosWorld, tolerance) {
    const intersectSet = [];
    for (let i = 0; i < objects.length; i++) {
        const ci = objects[i];
        const dr = ci.drawableWorld;

        if (dr.isInsideXY(mousePosWorld, tolerance)) {
            intersectSet.push({ obj: ci, index: i });
        }
    }

    intersectSet.sort((a, b) => {
        const obja = a.obj;
        const objb = b.obj;

        if (obja.options.z === objb.options.z) {
            return objb.id - obja.id;
        }

        return objb.z - obja.z;
    });
    return intersectSet;
}

function insidePoly(p, points) {
    const ci = i => MatF32.copy(mat.subvec(mat.col(points, i), 0, 2));

    const y = p => p.at(1);

    let wn = 0;    // the  winding number counter

    // loop through all edges of the polygon
    // edge from V[i] to  V[i+1]
    for (let i = 0; i < points.cols(); i++) {
        const vi = ci(i);
        const vip = ci((i + 1) % points.cols());

        if (y(vi) <= y(p)) {          // start y <= P.y
            if (y(vip) > y(p))      // an upward crossing
                if (isLeft(vi, vip, p) > 0)  // P left of  edge
                    ++wn;            // have  a valid up intersect
        }
        else {                        // start y > P.y (no test needed)
            if (y(vip) <= y(p))     // a downward crossing
                if (isLeft(vi, vip, p) < 0)  // P right of  edge
                    --wn;            // have  a valid down intersect
        }
    }
    return wn !== 0;
}

export {
    distanceSquaredPointLineSegment,
    transformPoints,
    computeFrustumPlanes,
    pointInBounds2d,
    computeBounds,
    intersectLinePlane,
    planeFromPointNormal,
    simplifyLine,
    transformPlane,
    pointSide,
    pointInside,
    mat2points,
    isClockwise,
    isLeft,
    normal2D,
    addz,
    closestVertex,
    findIntersectSet,
    insidePoly
};